/**
 * The field set a project is described by, drawn once for the dialog that
 * creates one and the dialog that corrects one.
 *
 * Until this file a project could be typed in and then never changed again: the
 * only mutations the section offered were the status pill and the progress
 * slider, so a typo in the name, a budget that was agreed at a different figure
 * and a deadline that moved were all permanent. Two separate forms would have
 * fixed that and drifted apart by the second edit - which is the state the
 * section was already in, since the create dialog never offered the start date
 * the API has always accepted, and the roadmap wrote that column as a side
 * effect of dragging a card.
 *
 * The client is the one difference between the two. `patchBody` in
 * `crm-projects/projects.routes.ts` omits `clientId` on purpose - moving a
 * project to another client would strand its invoices, estimates and expenses
 * on the old one - so in edit mode the field is shown disabled rather than
 * dropped: "whose project is this" should be answered in the same place both
 * times.
 *
 * State belongs to the caller, not here. The edit dialog has to compare what is
 * on screen against what it loaded so it can send only the keys that changed,
 * and a form that owned its own state would have to hand it back out anyway.
 * The conversions that comparison needs live in `projectFields.ts`, React-free
 * and re-exported below so a caller can import either from there or from here.
 *
 * The controls themselves are in `../_shared/formBits`. None of them knows what
 * a project is, and this file held them only because it is where the delivery
 * groups first needed them.
 */
import { DatePicker } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import type { BillingType } from "../../../lib/projects";
import { MenuField } from "../../ui";
import { ClientPicker } from "../_shared/ClientPicker";
import { Field, FoldGroup, TagChips, TextRow } from "../_shared/formBits";
import { UserPicker } from "../_shared/UserPicker";
import {
  DELIVERY_LIST_ENTRY_MAX,
  DELIVERY_LIST_MAX,
  DELIVERY_TEXT_MAX,
  DESCRIPTION_MAX,
  LINK_TEXT,
  LINK_TEXT_MAX,
  TAGS_MAX,
  TAG_MAX_LEN,
  type DeliveryTextField,
  type ProjectFormValues,
} from "./projectFields";

export {
  DESCRIPTION_MAX,
  blankProjectForm,
  centsOf,
  clearedFigure,
  hoursOf,
  projectCreateBody,
  projectPatch,
  projectToForm,
  type ProjectFormValues,
} from "./projectFields";

/** One of the single-line delivery or link columns, set by name. Typed against
 *  `DeliveryTextField`, so a row drawn from one of those lists can only write a
 *  column the form actually holds. */
function setColumn(v: ProjectFormValues, key: DeliveryTextField, next: string): ProjectFormValues {
  return { ...v, [key]: next };
}

/**
 * The fields themselves.
 *
 * Billing type comes before the money figures because it decides which of them
 * mean anything: an hourly project ignores the budget and a non-billable one
 * ignores both, so the field that does not apply is not offered rather than
 * offered and quietly discarded. The estimate in hours is not one of those - it
 * describes the work rather than the invoice, and is asked on every project.
 *
 * The two folded groups at the bottom are the delivery record and the links.
 * They are the same eleven columns `DeliveryCard` draws on the overview tab, and
 * they are here for the reason the card is there: an imported project arrives
 * with them filled and a project started here arrives with them empty, and a
 * column that can only be read is a column that goes stale the first time
 * anything about the site changes.
 */
export function ProjectForm({
  value,
  onChange,
  mode,
  figureHint,
}: {
  value: ProjectFormValues;
  onChange: (next: ProjectFormValues) => void;
  mode: "create" | "edit";
  /** One line under the figures, for the edit dialog to explain a figure it
   *  cannot clear. Nothing is drawn when it is absent. */
  figureHint?: string;
}) {
  const t = useT();
  const { locale } = useI18n();
  const set = (patch: Partial<ProjectFormValues>) => onChange({ ...value, ...patch });

  return (
    <>
      <Field label={t("projects.client")}>
        {mode === "edit" ? (
          // Disabled rather than absent. This is the one fact on the form the
          // route will not let this dialog change, and a field that vanishes
          // sends the operator looking for it somewhere else.
          <label className="crm-inp opacity-60">
            <input value={value.clientName} disabled />
          </label>
        ) : (
          <ClientPicker
            value={value.clientName}
            selectedId={value.clientId}
            onChange={(v) => set({ clientName: v, clientId: null })}
            onPick={(id, nm) => set({ clientId: id, clientName: nm })}
            placeholder={t("projects.clientPlaceholder")}
            required
          />
        )}
      </Field>

      <Field label={t("projects.name")}>
        <label className="crm-inp">
          <input
            value={value.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder={t("projects.namePlaceholder")}
            autoComplete="off"
            required
          />
        </label>
      </Field>

      <Field label={t("projects.description")}>
        <textarea
          className="crm-area"
          value={value.description}
          onChange={(e) => set({ description: e.target.value })}
          placeholder={t("projects.descriptionPlaceholder")}
          maxLength={DESCRIPTION_MAX}
        />
      </Field>

      {/* The app's own menu, not the browser's. A native `<select>` inside a
          `.crm-inp` pill draws no arrow - the pill hides it - and then drops a
          grey operating system list over a dialog this product painted, which
          is the one control on the form that still looked like somebody else's
          software. `MenuField` in its `form` dress is the same 52px sunken field
          the inputs around it are, and it is what the task and vault dialogs
          already use. It needs a `PopHost` above it; both project dialogs mount
          one (see `NewProjectModal` and `EditProjectModal`).

          An imported project carries no billing type at all and the route has no
          way to write one back to null, so the blank stays in the list as the
          current answer and is not offered as a choice: `MenuField` prints the
          option matching the value, and the three real options are the only ones
          drawn. An enabled blank would promise a clearing that cannot happen. */}
      <Field label={t("projects.billingType")}>
        <MenuField<BillingType | "">
          variant="form"
          value={value.billingType}
          onChange={(billingType) => set({ billingType })}
          label={t("projects.billingType")}
          options={[
            ...(value.billingType === "" ? [{ value: "" as const, label: "-" }] : []),
            { value: "fixed", label: t("projects.billing.fixed") },
            { value: "hourly", label: t("projects.billing.hourly") },
            { value: "none", label: t("projects.billing.none") },
          ]}
        />
      </Field>

      {/* Each field keeps its own wrapper div because `Field` is a fragment - a
          label and a control, two elements. Dropped straight into the grid they
          would be placed as two separate cells and the label would sit next to
          somebody else's input.

          The wrappers are rendered WITH their field rather than always: with
          three cells in a two-column grid, an always-present empty div would
          hold a track and leave a gap where a hidden field used to be. Flow
          placement instead - budget beside the estimate on a fixed-price
          project, the rate taking the second cell on an hourly one and pushing
          the estimate to its own row. */}
      <div className="grid grid-cols-2 gap-3">
        {/* A non-billable project has no budget to track against, so the field
            is not offered rather than offered and ignored. */}
        {value.billingType !== "none" ? (
          <div>
            <Field label={`${t("projects.budget")} (EUR)`}>
              <label className="crm-inp">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  value={value.budget}
                  onChange={(e) => set({ budget: e.target.value })}
                  placeholder="0"
                />
              </label>
            </Field>
          </div>
        ) : null}
        {value.billingType === "hourly" ? (
          <div>
            <Field label={`${t("projects.hourlyRate")} (EUR)`}>
              <label className="crm-inp">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  value={value.hourlyRate}
                  onChange={(e) => set({ hourlyRate: e.target.value })}
                  placeholder="0"
                />
              </label>
            </Field>
          </div>
        ) : null}
        {/* Offered on every project, including the non-billable ones. What the
            work was expected to take is a fact about the work, not about how it
            is charged; a favour done for free can still overrun, and the
            overview's planned-versus-actual row has nothing to say without this.

            `step="any"` rather than a figure. `step` is a VALIDATION rule, not
            just spinner granularity: `step="0.5"` would make 7.3 a stepMismatch
            and the browser would refuse to submit the dialog, explaining itself
            in a tooltip about "the two nearest valid values" that says nothing
            about projects. The column is a `Float?` and takes whatever was
            agreed, so the field refuses nothing either.

            No placeholder "0" like its neighbours, because zero is the one
            answer this field must not suggest: an empty box here means nobody
            estimated, and that is a different claim. */}
        <div>
          <Field label={t("projects.estimatedHours")}>
            <label className="crm-inp">
              <input
                type="number"
                step="any"
                min="0"
                inputMode="decimal"
                value={value.estimatedHours}
                onChange={(e) => set({ estimatedHours: e.target.value })}
              />
            </label>
          </Field>
        </div>
      </div>

      {figureHint ? <p className="mt-1.5 text-[11px] text-muted-foreground">{figureHint}</p> : null}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Field label={t("projects.startDate")}>
            <DatePicker
              value={value.startDate}
              onChange={(v) => set({ startDate: v })}
              locale={locale}
              placeholder={t("projects.startDate")}
            />
          </Field>
        </div>
        <div>
          <Field label={t("projects.deadline")}>
            <DatePicker
              value={value.deadline}
              onChange={(v) => set({ deadline: v })}
              locale={locale}
              placeholder={t("projects.deadline")}
            />
          </Field>
        </div>
      </div>

      {/* Who is on this project - the one place it can be answered.
          `Project.members` has been a column since the module was written and
          the route has always accepted it, but nothing in the product ever
          wrote to it, which is why the overview draws a team strip that has
          been empty on all twenty-three imported projects. It is asked here and
          not on the overview card for the reason that card states: membership
          is a field of the project record, and a second writing surface would
          be a second place to go wrong about one column.

          Placed between the dates and the tags because it answers WHO about a
          project rather than describing it. The folded groups below are the
          delivery record, and a person is not a delivery column.

          Always drawn, and empty says so in a sentence. That is the state
          almost every project is in today, and a search box with no chips under
          it reads as a control that failed to load rather than as the fact:
          nobody has been named. The picker offers the host site's staff, which
          on the day this shipped is one person - so a one-row panel is the whole
          register, not a search that found nothing. */}
      <Field label={t("projects.team")}>
        <UserPicker
          multiple
          value={value.members}
          onChange={(members) => set({ members })}
          placeholder={t("projects.members")}
        />
        {value.members.length === 0 ? (
          <p className="crm-mut mt-1.5 text-[12.5px]">{t("projects.noMembers")}</p>
        ) : null}
      </Field>

      <Field label={t("projects.tags")}>
        <TagChips
          tags={value.tags}
          onChange={(tags) => set({ tags })}
          placeholder={t("projects.tagsPlaceholder")}
          maxLen={TAG_MAX_LEN}
          maxCount={TAGS_MAX}
          removeLabel={t("common.delete")}
        />
      </Field>

      <FoldGroup
        title={t("projects.delivery")}
        openWhen={
          !!value.platform || !!value.logoStatus || !!value.pages || !!value.services.length || !!value.features.length
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <TextRow
            label={t("projects.platform")}
            value={value.platform}
            onChange={(platform) => set({ platform })}
            maxLength={DELIVERY_TEXT_MAX}
          />
          <TextRow
            label={t("projects.logoStatus")}
            value={value.logoStatus}
            onChange={(logoStatus) => set({ logoStatus })}
            maxLength={DELIVERY_TEXT_MAX}
          />
        </div>

        {/* The same chip control as the tags, at the ceilings the route states
            for these two columns rather than the tag ones. `servicesRaw` is not
            offered: it is what the old system said, and it is not the
            operator's to rewrite. */}
        <Field label={t("projects.services")}>
          <TagChips
            tags={value.services}
            onChange={(services) => set({ services })}
            placeholder={t("common.add")}
            maxLen={DELIVERY_LIST_ENTRY_MAX}
            maxCount={DELIVERY_LIST_MAX}
            removeLabel={t("common.delete")}
          />
        </Field>

        <Field label={t("projects.features")}>
          <TagChips
            tags={value.features}
            onChange={(features) => set({ features })}
            placeholder={t("common.add")}
            maxLen={DELIVERY_LIST_ENTRY_MAX}
            maxCount={DELIVERY_LIST_MAX}
            removeLabel={t("common.delete")}
          />
        </Field>

        {/* Text, not a number input. The old system recorded a range ("1-5",
            "10+"), and a number field would refuse to hold the answer we have. */}
        <TextRow
          label={t("projects.pages")}
          value={value.pages}
          onChange={(pages) => set({ pages })}
          maxLength={DELIVERY_TEXT_MAX}
        />
      </FoldGroup>

      {/* Drawn from `LINK_TEXT` rather than written out five times, and not to
          save the lines: it is the SAME list `projectPatch` walks, so a sixth
          link column cannot end up in the request body with no box to type it
          in. That drift is what this file's header is about. The labels are
          `projects.adminUrl`, `projects.hostingUrl`, `projects.dbAdminUrl`,
          `projects.cpanelUrl` and `projects.templateUrl`.

          Plain text boxes, no `type="url"`. The browser's URL validation would
          refuse the bare hostnames this data is full of, and refusing them
          loses the bookmark; whether a value can be followed is decided where
          it is rendered, by `panelHref` in `DeliveryCard.tsx`. The placeholder
          is the scheme rather than a sentence, because the scheme is the one
          thing worth adding while you are typing. */}
      <FoldGroup title={t("projects.links")} openWhen={LINK_TEXT.some((key) => !!value[key])}>
        {LINK_TEXT.map((key) => (
          <TextRow
            key={key}
            label={t(`projects.${key}`)}
            value={value[key]}
            onChange={(next) => onChange(setColumn(value, key, next))}
            maxLength={LINK_TEXT_MAX}
            placeholder="https://"
          />
        ))}
      </FoldGroup>
    </>
  );
}
